import { logger } from './logger'

/**
 * Performance Monitoring Utility
 * Para medir y optimizar el rendimiento de la aplicación
 */

export class PerformanceMonitor {
  private static timers: Map<string, number> = new Map()

  static startTimer(name: string): void {
    this.timers.set(name, performance.now())
  }

  static endTimer(name: string, metadata?: any): number {
    const startTime = this.timers.get(name)
    if (!startTime) {
      logger.warn(`Performance timer '${name}' was not started`)
      return 0
    }

    const duration = performance.now() - startTime
    this.timers.delete(name)
    
    logger.performance(name, Math.round(duration), metadata)
    
    // Alertar si algo es muy lento
    if (duration > 3000) {
      logger.warn(`Slow operation detected: ${name} took ${Math.round(duration)}ms`, metadata)
    }
    
    return duration
  }

  static measureAsync<T>(
    name: string, 
    fn: () => Promise<T>, 
    metadata?: any
  ): Promise<T> {
    this.startTimer(name)
    return fn().finally(() => {
      this.endTimer(name, metadata)
    })
  }

  static measureSync<T>(
    name: string, 
    fn: () => T, 
    metadata?: any
  ): T {
    this.startTimer(name)
    try {
      return fn()
    } finally {
      this.endTimer(name, metadata)
    }
  }
}

// Hook para medir rendimiento de componentes
export const usePerformance = (componentName: string) => {
  const startRender = () => {
    PerformanceMonitor.startTimer(`${componentName}_render`)
  }

  const endRender = () => {
    PerformanceMonitor.endTimer(`${componentName}_render`)
  }

  return { startRender, endRender }
}

// Métricas de uso de la aplicación
export const trackUserInteraction = (action: string, metadata?: any) => {
  logger.user(action, undefined, { 
    timestamp: Date.now(),
    ...metadata 
  })
}

// Métricas de API
export const trackAPICall = (method: string, endpoint: string, duration: number, status: number) => {
  logger.api(method, endpoint, status, duration)
}
